import {
  Component,
  OnDestroy,
  Input,
  ElementRef,
  EventEmitter,
  ViewChild,
  AfterViewInit,
  ViewChildren,
  QueryList,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { Menu } from "@bfx/primeng/menu";

import { BfxPopupNotificationCommand } from "./models/bfx-popup-notification-command.enum";
import { BfxPopupNotificationData } from "./models/bfx-popup-notification-data.model";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { BfxPopupNotificationService } from "./services/bfx-popup-notification.service";

import { LoggerService } from "@bfx/logger";
import { BFXNotificationType, BFXNotificationSubType } from "shared/cubiq-services";
import { BfxPopupAcceptanceNewNotificationData } from "app/bfx-notifications/models/bfx-popup-acceptance-new-notification-data.model";

const logger = LoggerService.for(__dirname);

// const ELEMENT_TYPE: string = "notificationType";

const ICON_INFO = "icon_ion_info_filled";
const ICON_CHECK = "icon_ion_check_circle_filled";
const ICON_WARNING = "icon_ion_warning_filled";
const ICON_ERROR = "icon_ion_error_filled";

type IconModelType = {
  /** Tooltip displayed on the icon */
  tooltip: string;

  /** Action callback when icon is clicked */
  clickAction: () => void;
};
const CLOSE_BUTTON_DEFAULT = "bfx-icon-button-on-dark-subtle-unsized";
const CLOSE_BUTTON_SCO = "bfx-icon-button-on-light-subtle-unsized";

const ACTION_BUTTON_DEFAULT = "bfx-icon-button-on-dark-moderate-unsized";
const ACTION_BUTTON_SCO = "bfx-icon-button-on-light-moderate-unsized";

const NOTIFICATION_ACTION_DEFAULT =
  "bfx-button-on-dark-moderate-unsized bfx-button-sm bfx-notification-label";
const NOTIFICATION_ACTION_SCO =
  "bfx-button-on-light-moderate-unsized bfx-button-sm bfx-notification-label";

@Component({
  selector: `bfx-popup-notification`,
  templateUrl: `./html/bfx-popup-notification.html`,
  standalone: false,
})
export class BfxPopupNotificationComponent implements OnDestroy {
  /***************************************/
  /* DATA */
  public _data: BfxPopupNotificationData;

  public isScoNotification = false;
  public isHcoNotification = false;
  public isBmarkFixingType = false;
  public isBmarkTriggered: boolean = false;
  public isDIType = false;
  public isConnectionMonitor = false;
  public isUnsolicited = false;
  public isAcceptanceNeededType = false;
  public showNotificationOrdersType = false;
  public isPositionNotification: boolean = false;
  public isStandardMessage: boolean;

  /** Icon class - determines the icon displayed */
  public iconClass: string = ICON_INFO;
  /** Notification class - full presentation style */
  public notificationClass:
    | "bfx-notification-info"
    | "bfx-notification-neutral"
    | "bfx-notification-positive"
    | "bfx-notification-negative"
    | "bfx-notification-soft-cutoff"
    | "bfx-notification-hard-cutoff" = "bfx-notification-info";
  public desktop: boolean = false;

  /** Close button style */
  public closeButtonClass: string = CLOSE_BUTTON_DEFAULT;
  public actionButtonClass: string = ACTION_BUTTON_DEFAULT;

  /** Classes for notification buttons */
  public buttonClasses = NOTIFICATION_ACTION_DEFAULT;

  // set by template, in rare cases to assist with parent window
  public _nativeDesktopParentWindowEmitter;
  private _nativeDesktopParentWindow$ = null;
  private static _nativeDesktop: boolean = false;

  /** Should the secondary footer message be displayed */
  public showFooter: boolean;
  // Pagination properties
  public pageSize: number = 10;
  public first: number = 0;
  public pageNo: number = 1;

  @ViewChild("menu", { read: Menu })
  public menuComponent: Menu;

  @ViewChildren("notificationElement", { read: ElementRef })
  notificationElement: QueryList<ElementRef>;
  isDestroyed: boolean = false;

  public get data(): BfxPopupNotificationData {
    return this._data;
  }

  @Input()
  public set data(value: BfxPopupNotificationData) {
    this._data = value;

    if (value != null) {
      value.updateActionCallback = this.onUpdateActions.bind(this);
    }

    this.constructMenuItems();
    this.initializeFlags();
    this.setupIconClass();
  }

  public menuItems: Array<any> = [];

  public notificationOpen: boolean = false;

  // Declare default class name for View button
  public toggleOrdersView = "icon-ion_chevron_down";
  public toggleOrdersText = "View Details";

  /** Defines data for the notification icon */
  public iconModel: IconModelType = {
    tooltip: null,
    clickAction: null,
  };

  // enum in template
  public BFXNotificationSubType = BFXNotificationSubType;
  public BFXNotificationType = BFXNotificationType;

  static isBmarkFixingType(data: BfxPopupNotificationData) {
    return (
      data.typeStr.startsWith("BMARK_SOFT_CUT_OFF") ||
      data.typeStr.startsWith("BMARK_HARD_CUT_OFF") ||
      data.typeStr.startsWith("BMARK_TRIGGERED")
    );
  }

  /**
   * Used to determine if we show Dealer Intervention option - date is DI type if type
   * begins with DEALER_INTERVENTION or payload has an isBmarkOrder
   * @param data
   * @returns
   */
  static isDIType(data: BfxPopupNotificationData): boolean {
    return (
      data.typeStr.startsWith("DEALER_INTERVENTION") ||
      (data instanceof BfxPopupAcceptanceNewNotificationData && data.isBmarkOrder)
    );
  }

  /**
   * Used to determine if we show Inbox option - date is DI type if type
   * begins with DEALER_INTERVENTION or payload has an isBmarkOrder
   * @param data
   * @returns
   */
  static isAcceptanceNeededType(data: BfxPopupNotificationData): boolean {
    return (
      data.typeStr.startsWith("ACCEPTANCE_NEEDED") &&
      !(data instanceof BfxPopupAcceptanceNewNotificationData && data.isBmarkOrder) &&
      !(data instanceof BfxPopupAcceptanceNewNotificationData && data.isAutoHedged)
    );
  }

  constructMenuItems(): void {
    this.menuItems = [];

    if (this._data && this._data.actions && this._data.actions.length > 0) {
      this._data.actions.forEach((action) => {
        if (action.separatorBefore) {
          this.menuItems.push({
            separator: true,
          });
        }
        this.menuItems.push({
          label: action.label,
          command: (event) => this._data.doAction(action.command),
          icon: action.icon,
          styleClass: action.styleClass,
        });
      });
    } else {
      this.menuItems = null;
    }
  }

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */
  constructor(
    private _translationService: TranslateService,
    private _notificationService: BfxPopupNotificationService,
    public element: ElementRef,
  ) {
    this.desktop = BfxPopupNotificationComponent._nativeDesktop;
  }

  static {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      BfxPopupNotificationComponent._nativeDesktop = true;
    }
  }

  ngOnDestroy() {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      if (this._nativeDesktopParentWindowEmitter != null) {
        this._nativeDesktopParentWindowEmitter.complete();
      }
    }
    this.isDestroyed = true;
  }

  /**********************************************************************/
  /* INITIALIZATION */

  initializeFlags(): void {
    //jmh
    if (this._data) {
      this.isAcceptanceNeededType = BfxPopupNotificationComponent.isAcceptanceNeededType(this.data);
      this.isDIType = BfxPopupNotificationComponent.isDIType(this.data);
      this.isScoNotification = this.data.typeStr.startsWith("BMARK_SOFT_CUT_OFF");
      this.isHcoNotification = this.data.typeStr.startsWith("BMARK_HARD_CUT_OFF");
      this.isBmarkTriggered = this.data.typeStr.startsWith("BMARK_TRIGGERED");
      this.isBmarkFixingType = BfxPopupNotificationComponent.isBmarkFixingType(this.data);
      this.isConnectionMonitor = this.data.typeStr.startsWith("CONNECTION_MONITOR");
      this.isUnsolicited = this.data.typeStr.startsWith("UNSOLICITED_");
      this.isPositionNotification = this.data.typeStr.startsWith("POSITION");
      this.showNotificationOrdersType = this.shouldShowNotificationOrders();

      this.isStandardMessage =
        !this.isBmarkFixingType &&
        !this.isDIType &&
        !this.isAcceptanceNeededType &&
        !this.isConnectionMonitor;

      this.showFooter =
        this.isStandardMessage ||
        this.isDIType ||
        this.isAcceptanceNeededType ||
        this.isUnsolicited;
    }
  }

  private setupIconClass(): void {
    if (this._data) {
      this.closeButtonClass = CLOSE_BUTTON_DEFAULT;
      this.actionButtonClass = ACTION_BUTTON_DEFAULT;
      this.buttonClasses = NOTIFICATION_ACTION_DEFAULT;

      switch (this._data.type) {
        case BFXNotificationType.ACTIVATING:
        case BFXNotificationType.ACTIVATED:
        case BFXNotificationType.CANCELLED:
        case BFXNotificationType.EXPIRED:
        case BFXNotificationType.EXPIRING:
        case BFXNotificationType.FILLED:
        case BFXNotificationType.MODIFIED:
        case BFXNotificationType.SUSPENDED:
        case BFXNotificationType.SUSPENDING:
        case BFXNotificationType.HOT:
        case BFXNotificationType.BMARK_CANCELLED:
        case BFXNotificationType.BMARK_MODIFIED:
        case BFXNotificationType.BMARK_FILLED:
        case BFXNotificationType.LIMITALGO_ORDER_ENTERED:
        case BFXNotificationType.BMARK_ORDER_ENTERED:
          // Neutral notifications
          this.iconClass = ICON_INFO;
          this.notificationClass = "bfx-notification-neutral";
          break;
        case BFXNotificationType.BMARK_NEW_ORDERS:
        case BFXNotificationType.DEALER_INTERVENTION:
        case BFXNotificationType.ACCEPTANCE_NEEDED:
          // Info notifications
          this.iconClass = ICON_INFO;
          this.notificationClass = "bfx-notification-info";
          break;
        case BFXNotificationType.TRIGGERED:
        case BFXNotificationType.BMARK_TRIGGERED:
          // Success notifications
          this.iconClass = ICON_CHECK;
          this.notificationClass = "bfx-notification-positive";
          break;
        case BFXNotificationType.BMARK_SOFT_CUT_OFF:
          // Need to explicitly change classes foo SCO to DArk on light
          this.closeButtonClass = CLOSE_BUTTON_SCO;
          this.actionButtonClass = ACTION_BUTTON_SCO;
          this.buttonClasses = NOTIFICATION_ACTION_SCO;
          this.iconClass = ICON_WARNING;
          this.notificationClass = "bfx-notification-soft-cutoff";
          break;
        case BFXNotificationType.UNSOLICITED_CANCEL:
        case BFXNotificationType.UNSOLICITED_PAUSE_RESUME:
          // Subtle Warn
          this.iconClass = ICON_WARNING;
          this.notificationClass = "bfx-notification-info";
          break;
        case BFXNotificationType.BMARK_HARD_CUT_OFF:
          // Negative bold
          this.iconClass = ICON_ERROR;
          this.notificationClass = "bfx-notification-hard-cutoff";
          break;
        case BFXNotificationType.DEAL_FAILURE:
        case BFXNotificationType.BMARK_DEAL_FAILURE:
        case BFXNotificationType.ALGO_INFLIGHT_FAILED:
        case BFXNotificationType.SESSION_END:
        case BFXNotificationType.STREAM_OFF:
          // Negative subtle
          this.iconClass = ICON_ERROR;
          this.notificationClass = "bfx-notification-negative";
          break;

        case BFXNotificationType.CONNECTION_MONITOR:
        case BFXNotificationType.CLEAR_NOTIFICATION:
        case BFXNotificationType.ORDER_ACTION_FAIL:
        case BFXNotificationType.TRADE_ACTION_FAIL:
        case BFXNotificationType.INTERVENTION_ACTION_FAIL:
        case BFXNotificationType.UNLOCK:
        case BFXNotificationType.PROFILE:
        case BFXNotificationType.EXPORT_IMPORT:
        case BFXNotificationType.ALGO_INFLIGHT:
        case BFXNotificationType.MESSAGE:
        case BFXNotificationType.POSITION:
        case BFXNotificationType.NETTING_FAILED:
        case BFXNotificationType.GENERIC_ERROR:
        case BFXNotificationType.UNSOLICITED_CANCEL_MIGRATED:
          // None of the above are supported by the spec
          logger.out.info("popnot-c - Defaulting notification icon to error - " + this._data.type);
          this.iconClass = ICON_ERROR;
          this.notificationClass = "bfx-notification-negative";
      }
    }
  }

  // private setupIconModel() {
  //   const payload = this.data?.payload?.length >= 1;
  //   if (payload && this.isAcceptanceNeededType) {
  //     this.iconModel.tooltip = "VIEW INBOX";
  //     this.iconModel.clickAction = () => this.showInbox();
  //   } else if (payload && this.isDIType) {
  //     this.iconModel.tooltip = "VIEW DEALER INTERVENTION";
  //     this.iconModel.clickAction = () => this.showIntervention();
  //   } else if (payload && this.isBmarkFixingType) {
  //     this.iconModel.tooltip = "VIEW BENCHMARK SUMMARY";
  //     this.iconModel.clickAction = () => this.showBenchmarkSummary();
  //   } else if (payload && this.isConnectionMonitor) {
  //     this.iconModel.tooltip = "VIEW CONNECTION MONITOR";
  //     this.iconModel.clickAction = () => this.showConnectionMonitor();
  //   } else if (payload && this.isunsolicitedCancel) {
  //     this.iconModel.tooltip = "VIEW OPS INTERVENTION";
  //     this.iconModel.clickAction = () => this.showOpsIntervention();
  //   } else {
  //     this.iconModel.tooltip = this.desktop ? "ACTIVATE OMS WINDOW" : "CLOSE";
  //     this.iconModel.clickAction = () => this.close(true);
  //   }
  // }

  public action(
    command: BfxPopupNotificationCommand,
    itemId?: string,
    close: boolean = true,
  ): void {
    this.data.doAction(command, itemId);
    if (close) {
      this.close();
    }
  }

  public tableAction(payloadItem) {
    if (payloadItem.as_no_action !== true && payloadItem.as_notify_footerAction) {
      this.action(payloadItem.as_notify_footerAction, payloadItem.as_notify_id, false);
    }
  }

  public doIconAction(): void {
    if (this.iconModel.clickAction != null) {
      this.iconModel.clickAction();
    }
  }

  close(focusWindow: boolean = false, closeAction = true): Promise<void> {
    return new Promise((resolve) => {
      if (this.data.closeAction && closeAction) {
        this.data.doAction(this.data.closeAction, null);
      }

      if (NativeDesktopSupport.isNativeDesktopContainer() && focusWindow) {
        NativeDesktopSupport.activateAllWindows().then(() => {
          this.executeAction(closeAction);
        });
      } else {
        this.executeAction(closeAction);
      }

      resolve();
    });
  }

  protected executeAction(closeAction): void {
    if (this.data.closeAction && closeAction) {
      this.data.doAction(this.data.closeAction, null);
    }

    this.data.doAction(BfxPopupNotificationCommand.POPUP_CLOSE);
  }

  showNotificationOrders() {
    this.notificationOpen = !this.notificationOpen;
    if (this.notificationOpen) {
      this.toggleOrdersView = "icon-ion_chevron_up";
      this.toggleOrdersText = "Hide Details";
    } else {
      this.toggleOrdersView = "icon-ion_chevron_down";
      this.toggleOrdersText = "View Details";
    }
  }

  showInbox(): void {
    this.close(true).then(() => {
      this._notificationService.showInbox();
    });
  }

  showIntervention(): void {
    this.close(true).then(() => {
      this._notificationService.showIntervention();
    });
  }

  showBenchmarkSummary(): void {
    this.close(true).then(() => {
      this._notificationService.showBenchmarkSummary(
        this.data.type,
        this.data.payload[0].fixingData,
        this.data.payload.length > 1,
      );
    });
  }

  showConnectionMonitor(): void {
    this.close(true).then(() => {
      this._notificationService.showConnectionMonitor();
    });
  }

  // showOpsIntervention(): void {
  //   this._notificationService.showOpsIntervention(
  //     BFXNotificationType.UNSOLICITED_CANCEL,
  //     this.data.payload[0].opsId,
  //   );
  //   this.close(true);
  // }

  public shouldShowNotificationOrders(): boolean {
    return (
      !BfxPopupNotificationComponent.isAcceptanceNeededType(this.data) &&
      !BfxPopupNotificationComponent.isDIType(this.data) &&
      !BfxPopupNotificationComponent.isBmarkFixingType(this.data)
    );
  }

  /**
   * The method is replacement for toggling menus that
   * explicitly recreates the context menu items when the menu is
   * toggled on or off. The reason for this is that if the
   * actions in the context menu have changed (for ex. with a call to <i>BfxPopupNoticitaionData::updateAllAvailableActions</i>) the
   * changes will not be reflected in the menu
   * @param menu the context menu that needs to be toggled
   * @param event the event that causes the toggle
   */
  public toggleMenu(menu: any, event: any): void {
    this.constructMenuItems();
    menu.toggle(event);
  }

  /**
   * Invoked after update allowable actions is called on notification
   * data which support this (supportActionCallback is set to true)
   */
  public onUpdateActions(): void {
    if (this.menuComponent != null) {
      this.menuComponent.hide();
    }
  }

  /**
   * Can only put in a foot if we have something in it otherwise the empty footer
   * will cause a gap
   * If updating the footer, developers need to add any new conditions here to make the footer visible
   */
  public hasFooterParts(): boolean {
    if (this.isConnectionMonitor) {
      return true;
    }
    if (this.data?.payload && this.data.payload.length > 0) {
      if (this.data.payload.length == 1) {
        return this.data.footerAction && !this.data.payload[0].as_notify_ref;
      } else {
        return this.showNotificationOrdersType || this.isAcceptanceNeededType || this.isDIType;
      }
    }
    return false;
  }

  // Pagination methods
  public isFirstPage(): boolean {
    return this.pageNo == 1;
  }
  public isLastPage(): boolean {
    return this.pageNo == this.pageCount;
  }

  public previousPage(): void {
    if (this.pageNo > 1) {
      this.pageNo--;
      this.first -= this.pageSize;
    }
  }
  public nextPage(): void {
    if (this.pageNo < this.pageCount) {
      this.pageNo++;
      this.first += this.pageSize;
    }
  }

  public get pageCount(): number {
    if (this._data.payload?.length > 0) {
      return Math.ceil(this._data.payload.length / this.pageSize);
    }
    return 1;
  }

  public get showSecondaryRow(): boolean {
    /*
           <div class="bfx-notification-body-secondary">
          <div class="bfx-notification-body-secondary-left">
            {{ (data.payload&&data.payload.length==1 && showFooter)?data.footer:"" }}
          </div>
          <div
            class="bfx-notification-body-secondary-right"
            [ngClass]="{'bfx-notify-actionable':data.footerAction && !data.payload[0].as_no_action}"
            *ngIf="data.payload&&data.payload.length==1"
            (click)="tableAction(data.payload[0])"
            [nativeDesktopStyle]="{ '-webkit-app-region' : 'no-drag' }"
          >
            {{data.payload[0].as_notify_ref}}
          </div>
        </div>

    */
    if (this.data?.payload?.length == 1) {
      if (
        (this.showFooter && this.data.footer?.length > 0) ||
        (this.data.payload[0]?.as_no_footer != true && this.data.payload[0].as_notify_ref)
      ) {
        return true;
      }
    }
    return false;
  }
}
